<script lang="ts">
  // Die Seitenleiste (docs/shell.md).
  //
  // Zeichnet einen Baum und sonst nichts: WAS drinsteht, entscheidet
  // tree.svelte.ts, wie ein einzelner Knoten aussieht TreeNode.svelte.
  // Diese Datei kennt keine Domaene, keinen Store und keinen Pfad --
  // sie haelt nur den Aufklapp-Zustand und den Kopf.

  import AnordnenLeiste from "../AnordnenLeiste.svelte";
  import EinstellungZahnrad from "../EinstellungZahnrad.svelte";
  import GriffPunkte from "../GriffPunkte.svelte";
  import Icon from "../Icon.svelte";
  import { pressReorder } from "../press-reorder";
  import GanzeZeilen from "./GanzeZeilen.svelte";
  import Griff from "./Griff.svelte";
  import Lage from "../Lage.svelte";
  import { t } from "../i18n.svelte";
  import { getGeraetPref, getPref, setGeraetPref, setPref } from "../prefs.svelte";
  import { navSections, type SectionId } from "../nav-sections";
  import TreeNode from "./TreeNode.svelte";
  import { zusatzFuer, type ExplorerAbschnitt } from "./explorer-zusatz";
  import { BaumOrdnen, baueBaum, type BaumKnoten } from "./tree.svelte";

  interface Props {
    sektion: SectionId;
    /** Breite in Pixeln -- ziehbar, gemerkt im Chrome-Zustand. */
    breite: number;
  }
  let { sektion, breite }: Props = $props();

  // Gemerkt wird der ZUSTAND je Knoten, nicht "dieser ist zu".
  //
  // Vorher stand hier eine Liste der zugeklappten Gruppen -- und damit
  // liess sich ein Knoten, der von sich aus zu ist, NIE oeffnen: der
  // Klick nahm ihn aus einer Liste, in der er gar nicht stand, und der
  // Default hielt ihn weiter zu (Tim 15.08.). Ein Merk-Modell braucht
  // drei Zustaende: offen, zu, und "noch nie angefasst" (= Default).
  const PREF_KEY = "shell_explorer_gruppen";

  const baum = $derived(baueBaum(sektion));
  const sec = $derived(navSections.find((s) => s.id === sektion));
  const titel = $derived(sec?.label ?? "");

  const zustand = $derived(getPref<Record<string, boolean>>(PREF_KEY) ?? {});

  function schluessel(id: string): string {
    return sektion + "/" + id;
  }
  function istAufgeklappt(k: BaumKnoten): boolean {
    const gemerkt = zustand[schluessel(k.id)];
    if (typeof gemerkt === "boolean") return gemerkt;
    return k.standardOffen === true;
  }
  function klappe(k: BaumKnoten): void {
    setPref(PREF_KEY, { ...zustand, [schluessel(k.id)]: !istAufgeklappt(k) });
  }

  // Anordnen: gedrückt halten an einer Zeile (tree.svelte.ts, BaumOrdnen).
  // Wechselt der Bereich, endet der Modus -- seine Ebene gibt es dort nicht.
  const ordnen = new BaumOrdnen(() => sektion);
  $effect(() => {
    void sektion;
    ordnen.ebene = null;
  });
  const wurzelIds = $derived(baum.map((k) => k.id));
  // Kopf-Handgriff und feste Fläche unten, je Bereich (explorer-zusatz.ts).
  const zusatz = $derived(zusatzFuer(sektion));

  // Abschnitte (explorer-zusatz.ts, docs/shell.md): oben die, die den Rest
  // füllen (Chats), unten angedockt die übrigen (Änderungen, Verbrauch) --
  // in eigener Reihenfolge (am Kopf gedrückt halten, wie bei Baumzeilen) und
  // eigener Höhe (Griff darüber; Tim 16.10.: „immer ganz unten andocken …
  // verschoben werden können, auch in der Höhe änderbar"). Auf/Zu,
  // Reihenfolge und Höhen merkt sich jedes GERÄT (prefs-geraet.ts): auf dem
  // MacBook fehlt der Platz, den der iMac hat.
  interface AbschnittMerker {
    offen?: Record<string, boolean>;
    hoehe?: Record<string, number>;
    reihe?: string[];
  }
  const ABSCHNITT_KEY = "shell_abschnitte";
  const alleMerker = $derived(getGeraetPref<Record<string, unknown>>(ABSCHNITT_KEY) ?? {});
  const merker = $derived.by<AbschnittMerker>(() => {
    const m = alleMerker[sektion];
    return m && typeof m === "object" ? (m as AbschnittMerker) : {};
  });
  function schreibeMerker(neu: AbschnittMerker): void {
    // Je Bereich ein Eintrag; flache Reste der ersten Fassung fallen weg.
    const rein = Object.fromEntries(Object.entries(alleMerker).filter(([, v]) => v && typeof v === "object"));
    setGeraetPref(ABSCHNITT_KEY, { ...rein, [sektion]: neu });
  }
  function abschnittOffen(a: ExplorerAbschnitt): boolean {
    const gemerkt = merker.offen?.[a.id];
    return typeof gemerkt === "boolean" ? gemerkt : a.standardOffen === true;
  }
  function klappeAbschnitt(a: ExplorerAbschnitt): void {
    schreibeMerker({ ...merker, offen: { ...merker.offen, [a.id]: !abschnittOffen(a) } });
  }

  const alleAbschnitte = $derived(zusatz.abschnitte ?? []);
  const oben = $derived(alleAbschnitte.filter((a) => !a.andocken));
  const unten = $derived.by(() => {
    const angedockt = alleAbschnitte.filter((a) => a.andocken);
    // Die gemerkte Reihenfolge; was darin fehlt (ein neuer Abschnitt),
    // rückt hinter seinen Vorgänger aus der Grundordnung, nicht ans Ende.
    const ids = (merker.reihe ?? []).filter((id) => angedockt.some((a) => a.id === id));
    angedockt.forEach((a, i) => {
      if (ids.includes(a.id)) return;
      const vorgaenger = angedockt.slice(0, i).reverse().find((x) => ids.includes(x.id));
      ids.splice(vorgaenger ? ids.indexOf(vorgaenger.id) + 1 : 0, 0, a.id);
    });
    return ids.map((id) => angedockt.find((a) => a.id === id)!);
  });
  const obenOffen = $derived(oben.some(abschnittOffen));

  // Höhe: während des Zugs nur hier, gemerkt erst, wenn der Zug kurz ruht --
  // sonst ginge jede Mausbewegung als Einstellung an den Server.
  let liveHoehe = $state<Record<string, number>>({});
  let flaecheEl = $state<HTMLDivElement | undefined>(undefined);
  const abschnittEls: Record<string, HTMLElement> = {};
  let merkTimer: ReturnType<typeof setTimeout> | undefined;
  function hoeheVon(id: string): number | undefined {
    return liveHoehe[id] ?? merker.hoehe?.[id];
  }
  function setzeHoehe(id: string, px: number): void {
    const max = Math.max(80, (flaecheEl?.clientHeight ?? 600) - 90);
    liveHoehe = { ...liveHoehe, [id]: Math.round(Math.min(max, Math.max(56, px))) };
    clearTimeout(merkTimer);
    merkTimer = setTimeout(() => {
      schreibeMerker({ ...merker, hoehe: { ...merker.hoehe, ...liveHoehe } });
      liveHoehe = {};
    }, 400);
  }

  // Reihenfolge der angedockten: gedrückt halten am Kopf öffnet den Modus.
  let abschnittOrdnen = $state(false);
  $effect(() => {
    void sektion;
    abschnittOrdnen = false;
  });
  $effect(() => {
    if (ordnen.ebene !== null) abschnittOrdnen = false;
  });
  function verschiebeAbschnitt(von: string, nach: string): void {
    const ids = unten.map((a) => a.id);
    const i = ids.indexOf(von);
    const j = ids.indexOf(nach);
    if (i < 0 || j < 0 || i === j) return;
    ids.splice(i, 1);
    ids.splice(j, 0, von);
    schreibeMerker({ ...merker, reihe: ids });
  }
  const untenAngepasst = $derived(!!merker.reihe?.length || Object.keys(merker.hoehe ?? {}).length > 0);
</script>

{#snippet abschnitt(a: ExplorerAbschnitt, angedockt: boolean)}
  {@const offen = abschnittOffen(a)}
  {@const lage = a.lage && !(offen && a.lageNurZu) ? a.lage() : []}
  {@const h = angedockt && offen ? hoeheVon(a.id) : undefined}
  {#if angedockt && offen}
    <Griff
      achse="y"
      punkte={false}
      label={t("shell.abschnitt_hoehe", "Höhe von „{name}“ ändern").replace("{name}", a.titel())}
      wert={h ?? 0}
      min={56}
      max={800}
      onzug={(pos) => {
        const el = abschnittEls[a.id];
        if (el) setzeHoehe(a.id, el.getBoundingClientRect().bottom - pos.y);
      }}
      onschritt={(d) => setzeHoehe(a.id, (abschnittEls[a.id]?.offsetHeight ?? 120) + d)}
    />
  {/if}
  <section
    class="abschnitt flex min-h-0 flex-col"
    class:fuellt={!angedockt && offen}
    class:frei={angedockt && offen && h === undefined}
    style:flex={h !== undefined ? `0 1 ${h}px` : undefined}
    aria-label={a.titel()}
    bind:this={abschnittEls[a.id]}
    use:pressReorder={{
      group: "abschnitte:" + sektion,
      id: a.id,
      armed: abschnittOrdnen,
      aus: !angedockt || unten.length < 2,
      griff: ".abschnitt-kopf",
      onActivate: () => {
        ordnen.ebene = null;
        abschnittOrdnen = true;
      },
      onReorder: (_g, von, nach) => verschiebeAbschnitt(von, nach),
      liftClass: "anordnung-zieht",
    }}
  >
    <div class="abschnitt-kopf flex items-center gap-1 pr-2">
      <button
        type="button"
        class="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 pl-1.5 text-left"
        aria-expanded={offen}
        aria-label={lage.length ? `${a.titel()}: ${lage.map((l) => l.text).join(", ")}` : undefined}
        onclick={() => klappeAbschnitt(a)}
      >
        {#if abschnittOrdnen && angedockt}
          <span class="grid w-3 flex-none place-items-center"><GriffPunkte hoehe={10} class="text-accent" /></span>
        {:else}
          <Icon name="chevron-right" size={12} class="text-fg-subtle flex-none {offen ? 'rotate-90' : ''}" />
        {/if}
        <span class="t-eyebrow text-fg-subtle flex-none">{a.titel()}</span>
        {#if !offen && a.zu}
          {@const Zu = a.zu}
          <span class="ml-1 flex min-w-0 flex-1"><Zu {...a.zuProps ?? {}} /></span>
        {:else if lage.length}<span class="t-meta text-fg-muted ml-1 min-w-0 truncate"><Lage teile={lage} /></span>{/if}
      </button>
      {#if a.kopf}
        {@const Kopf = a.kopf}
        <span class="-my-1"><Kopf /></span>
      {/if}
    </div>
    {#if offen}
      <GanzeZeilen class="inhalt">
        {#if a.inhalt}
          {@const Inhalt = a.inhalt}
          <Inhalt fest={h !== undefined} />
        {:else}
          <div class="pb-2">{@render baumListe()}</div>
        {/if}
      </GanzeZeilen>
    {/if}
  </section>
{/snippet}

{#snippet baumListe()}
  {#each baum as knoten (knoten.id)}
    <TreeNode {knoten} tiefe={0} {istAufgeklappt} {klappe} {ordnen} eltern="" geschwister={wurzelIds} />
  {:else}
    <p class="text-fg-subtle t-meta px-3 py-2" data-zeile>
      {t("shell.tree_empty", "Hier gibt es nichts zu zeigen.")}
    </p>
  {/each}
{/snippet}

<aside
  class="explorer border-border flex flex-col overflow-hidden rounded-md border"
  style="width: {breite}px"
  aria-label={t("shell.explorer", "Übersicht")}
  data-bereich={sektion}
>
  <!-- Nur der Name des Bereichs. Bis 02.10.2026 öffnete der Kopf eine
       Übersichtsseite -- doppelt zu diesem Baum, und hinter ihm
       zurückgeblieben (Tim: „kann doch weg"). -->
  <div class="kopfzeile text-fg-subtle flex w-full items-center gap-2 px-3 pb-1 pt-2">
    <span class="ort-marke" aria-hidden="true"></span>
    <span class="t-eyebrow flex-1 truncate text-left">{titel}</span>
    <span class="-my-1 -mr-1.5"><EinstellungZahnrad bereich={sektion} /></span>
    {#if zusatz.kopf}
      {@const Kopf = zusatz.kopf}
      <span class="-my-1 -mr-1.5"><Kopf /></span>
    {/if}
  </div>

  <!-- Feste Seiten des Bereichs (explorer-zusatz.ts, `seiten`): Baumzeilen
       vor den Abschnitten -- Claude: Übersicht und Gedächtnis. -->
  {#if zusatz.seiten}
    {@const seiten = zusatz.seiten()}
    <div class="seiten pb-1">
      {#each seiten as k (k.id)}
        <TreeNode knoten={k} tiefe={0} {istAufgeklappt} {klappe} {ordnen} eltern="seiten" geschwister={[]} />
      {/each}
    </div>
  {/if}

  {#if ordnen.ebene !== null}
    {@const ebene = ordnen.ebene}
    <AnordnenLeiste
      schmal
      class="mx-2 mb-1"
      text={t("shell.baum_anordnen", "Zeilen ziehen, um sie neu anzuordnen")}
      angepasst={ordnen.angepasst(ebene)}
      onzuruecksetzen={() => ordnen.zuruecksetzen(ebene)}
      onfertig={() => (ordnen.ebene = null)}
    />
  {/if}

  {#if abschnittOrdnen}
    <AnordnenLeiste
      schmal
      class="mx-2 mb-1"
      text={t("shell.abschnitte_anordnen", "Abschnitte ziehen, um sie neu anzuordnen")}
      angepasst={untenAngepasst}
      onzuruecksetzen={() => schreibeMerker({ offen: merker.offen })}
      onfertig={() => (abschnittOrdnen = false)}
    />
  {/if}

  {#if zusatz.abschnitte}
    <div class="flex min-h-0 flex-1 flex-col" bind:this={flaecheEl}>
      {#each oben as a (a.id)}
        {@render abschnitt(a, false)}
      {/each}
      {#if !obenOffen}<div class="flex-1" aria-hidden="true"></div>{/if}
      {#each unten as a (a.id)}
        {@render abschnitt(a, true)}
      {/each}
    </div>
  {:else}
    <GanzeZeilen class="flex-1">
      <div class="pb-3">{@render baumListe()}</div>
    </GanzeZeilen>
  {/if}
  {#if zusatz.fuss}
    {@const Fuss = zusatz.fuss}
    <Fuss />
  {/if}
</aside>

<style>
  .explorer {
    flex: none;
    /* Chrome, nicht Inhalt: der Kasten liegt zwischen Grund und Editor,
       im Hellen dicht am Editor, im Dunkeln dicht am Grund -- wie in
       VS Code. Ton in tokens-light.css/tokens-dark.css. */
    background: var(--shell-kasten, var(--bg));
  }
  /* Die festen Seiten trennt dieselbe Linie von den Abschnitten. */
  .seiten {
    border-bottom: 1px solid var(--border);
  }
  /* Abschnitte: eine Linie trennt sie, wie die Flächen einer Seite. */
  .abschnitt:not(:first-child) {
    border-top: 1px solid var(--border);
  }
  /* Zugeklappt nur der Kopf. Oben füllt der offene den Rest (aber nie unter
     5rem); unten angedockt: ohne gezogene Höhe so hoch wie der Inhalt, bis
     45 % -- mit gezogener Höhe diese (style flex), schrumpfbar, wenn das
     Fenster kleiner wird. */
  .abschnitt {
    flex: none;
  }
  .abschnitt.fuellt {
    flex: 1 1 0;
    min-height: 5rem;
  }
  .abschnitt.frei {
    flex: 0 1 auto;
    max-height: 45%;
  }
  .abschnitt :global(.inhalt) {
    flex: 1 1 auto;
  }
  .abschnitt:global(.anordnung-zieht) {
    background: var(--bg-elev);
    border-radius: 0.4rem;
  }
  .abschnitt-kopf button:hover {
    color: var(--fg);
  }
  /* „Bereichsfarbe als Ort": ein kleines Feld vor dem Namen des Bereichs. */
  .ort-marke {
    display: none;
    width: 0.45rem;
    height: 0.45rem;
    border-radius: 2px;
    background: var(--bereich);
    flex: none;
  }
  :global(html[data-farbe-ort="1"]) .ort-marke {
    display: block;
  }
</style>
